package com.zi.support.shared

/**
 * Rules for copying meetings into the signed-in person's calendar and showing their other events.
 * Android/iOS write to the phone calendar, web to Google Calendar; all three tag events with [marker]
 * so a copy is never added twice and never shown as an "other" calendar entry.
 */
object CalendarSync {
    const val SITE_VISIT = "siteVisit"
    /** Meetings starting within this many days are copied into the calendar. */
    const val SYNC_DAYS = 90
    /** Other calendar entries shown on the Meetings screen, from today. */
    const val SHOW_DAYS = 14
    // ponytail: meetings only store a start time; every calendar copy is one hour until meetings get an end.
    const val DURATION_MILLIS = 60L * 60_000L
    private const val TAG = "SupportApp meeting #"

    fun marker(meetingId: String): String = "$TAG$meetingId"

    /** The meeting id tagged in a calendar event's description/notes, or null for the person's own events. */
    fun meetingIdIn(description: String?): String? =
        description?.substringAfter(TAG, "")?.takeWhile { !it.isWhitespace() }?.ifEmpty { null }

    /** Whether the signed-in person booked, owns or attends the meeting. */
    fun involves(uid: String, assignedToUid: String, createdByUid: String, attendeeUids: List<String>): Boolean =
        uid.isNotEmpty() && (uid == assignedToUid || uid == createdByUid || uid in attendeeUids)

    fun eventTitle(kind: String, title: String, customerName: String): String =
        if (kind == SITE_VISIT) listOf("Site visit", customerName, title).filter { it.isNotBlank() }.joinToString(" · ") else title

    fun eventDescription(meetingId: String, reason: String): String =
        listOf(reason.trim(), "Booked in the Support App.", marker(meetingId)).filter { it.isNotEmpty() }.joinToString("\n\n")

    /** Only meetings ahead of now, inside the sync window, involving the person, that are not already copied. */
    fun needsCopy(startAtMillis: Long, nowMillis: Long, alreadyCopied: Boolean): Boolean =
        !alreadyCopied && startAtMillis > nowMillis && startAtMillis <= nowMillis + SYNC_DAYS * DAY

    private const val DAY = 24L * 60L * 60_000L
}
